import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ConfirmDialog, useToast } from '../../../components/ui';
import { customerFilter, dateFilter, optionsFilter } from '../../../components/common/ListPage/listFilters';
import { sendOnWhatsApp } from '../../../lib/share';
import { Cell, ListPage } from '../../../components/common/ListPage/ListPage';
import { StatusBadge } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { INVOICE_STATUS, PAYMENT_STATUS } from '../../../constants/status';
import { labelOf } from '../../../utils/labels';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { invoiceService } from '../services/invoiceService';
import type { Invoice } from '../types/invoiceTypes';

const TABS = [{ value: '', label: 'All' }, ...Object.entries(INVOICE_STATUS).map(([value, [label]]) => ({ value, label }))];

export default function InvoiceList() {
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [deleting, setDeleting] = useState<{ i: Invoice; reload: () => void } | null>(null);
  const add = <Button leftIcon="plus" onClick={() => navigate('/invoices/new')}>New invoice</Button>;

  const rowActions = (i, { reload }) =>
    [
      { label: 'View', icon: 'eye', onClick: () => navigate(`/invoices/${i._id}`) },
      i.status === 'draft' && { label: 'Edit', icon: 'edit', onClick: () => navigate(`/invoices/${i._id}/edit`) },
      i.status === 'draft' && {
        label: 'Issue invoice',
        icon: 'checkCircle',
        onClick: async () => (await run('fin', () => invoiceService.finalize(i._id), 'Invoice finalized')) && reload(),
      },
      { label: 'Download PDF', icon: 'download', onClick: () => run('pdf', () => invoiceService.openPdf(i._id)) },
      i.status === 'final' && {
        label: 'Send on WhatsApp',
        icon: 'send',
        onClick: () =>
          run('wa', () => sendOnWhatsApp('invoices', i._id, i.customer?.phone, `Hello ${i.customer?.name}, please find invoice ${i.invoiceNo} for ${money(i.grandTotal)}.${i.balanceAmount > 0 ? ` Balance due: ${money(i.balanceAmount)}.` : ''}`)),
      },
      i.status === 'final' && i.balanceAmount > 0 && { label: 'Record payment', icon: 'rupee', onClick: () => navigate(`/transactions/new?category=invoice_payment&invoiceId=${i._id}`) },
      i.status === 'draft' && { divider: true },
      i.status === 'draft' && { label: 'Delete draft', icon: 'trash', danger: true, onClick: () => setDeleting({ i, reload }) },
    ].filter(Boolean);

  const remove = async () => {
    if (!deleting) return;
    if (await run('delete', () => invoiceService.remove(deleting.i._id), 'Draft deleted')) deleting.reload();
    setDeleting(null);
  };

  return (
    <>
      <ListPage
        title="Invoices"
        path="/invoices"
        tabs={TABS}
        filters={[
          dateFilter(),
          customerFilter,
          optionsFilter('paymentStatus', 'Payment', PAYMENT_STATUS, 'rupee'),
          { key: 'overdue', label: 'Due', icon: 'clock', options: [{ value: 'true', label: 'Overdue' }] },
          { key: 'invoiceType', label: 'Type', icon: 'receipt', options: [{ value: 'gst', label: 'GST invoice' }, { value: 'non_gst', label: 'Non-GST' }] },
        ]}
        searchPlaceholder="Invoice no, customer"
        actions={add}
        rowTo={(i) => `/invoices/${i._id}`}
        rowActions={rowActions}
        columns={[
          { header: 'Invoice no.', sort: 'invoiceNo', sortDesc: true, render: (i) => i.invoiceNo ?? 'Draft', csv: (i) => i.invoiceNo ?? 'Draft' },
          { header: 'Customer', sort: 'customer.name', render: (i) => <Cell title={i.customer?.name} sub={i.invoiceType === 'gst' ? 'GST invoice' : 'Non-GST'} />, csv: (i) => i.customer?.name },
          { header: 'Status', sort: 'status', render: (i) => <StatusBadge map={INVOICE_STATUS} value={i.status} />, csv: (i) => labelOf(INVOICE_STATUS, i.status) },
          { header: 'Payment', sort: 'paymentStatus', render: (i) => (i.status === 'final' ? <StatusBadge map={PAYMENT_STATUS} value={i.paymentStatus} /> : <span className="text-ink-300">-</span>), csv: (i) => labelOf(PAYMENT_STATUS, i.paymentStatus) },
          { header: 'Date', sort: 'date', sortDesc: true, render: (i) => formatDate(i.date), csv: (i) => formatDate(i.date) },
          { header: 'Due date', sort: 'dueDate', sortDesc: true, render: (i) => (i.dueDate ? formatDate(i.dueDate) : '-'), csv: (i) => formatDate(i.dueDate), defaultHidden: true },
          { header: 'Total', sort: 'grandTotal', sortDesc: true, align: 'right', render: (i) => <span className="font-semibold text-navy-900">{money(i.grandTotal)}</span>, csv: (i) => i.grandTotal / 100 },
          {
            header: 'Balance', sort: 'balanceAmount', sortDesc: true,
            align: 'right',
            render: (i) => (i.status === 'final' && i.balanceAmount > 0 ? <span className="font-medium text-red-600">{money(i.balanceAmount)}</span> : <span className="text-ink-300">-</span>),
            csv: (i) => (i.balanceAmount ?? 0) / 100,
          },
        ]}
        mobile={(i) => ({
          title: i.customer?.name,
          subtitle: [i.invoiceNo ?? 'Draft', formatDate(i.date)].join(' · '),
          right: money(i.grandTotal),
          badge: i.status === 'final' ? <StatusBadge map={PAYMENT_STATUS} value={i.paymentStatus} /> : <StatusBadge map={INVOICE_STATUS} value={i.status} />,
        })}
        empty={{ icon: 'receipt', title: 'No invoices yet', message: 'Create an invoice from a completed job, or make one directly.', action: add }}
      />
      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} onConfirm={remove} loading={busy === 'delete'} danger title="Delete this draft invoice?" message="The draft will be removed for good." confirmLabel="Delete draft" />
    </>
  );
}
